import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import { apiMutate } from '../composables/apiMutate'

export interface OutputSkill {
  slug: string
  description: string
  inputs: string[]
}

export interface CitationRef {
  path: string
  title: string
}

export interface DraftResult {
  skill: string
  target: string
  draft: string
  citations: CitationRef[]
  weak_citation: boolean
}

// Backs the /output-skills page. Two halves:
//
//   - the installed-skill catalog (GET /api/v1/output_skills), via
//     useAsyncResource so the page gets the standard loaded / loading /
//     needsAuth lifecycle the other admin views use.
//   - draftOutput(): POST /api/v1/output_skills/draft to run a skill
//     against a target. Read-through — the result is transient (nothing
//     is persisted), so there's no list to splice and no channel to
//     subscribe to, unlike the /synthesis review queue.
export const useOutputSkillsStore = defineStore('outputSkills', () => {
  const {
    data: skills,
    loaded,
    loading,
    error: loadError,
    needsAuth,
    refresh: fetchSkills,
  } = useAsyncResource<OutputSkill[]>(
    async () => {
      const res = await fetch('/api/v1/output_skills', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load output skills: ${res.status}`)
      return res.json()
    },
    { initialValue: [] },
  )

  async function draftOutput(skill: string, target: string, notes: string): Promise<DraftResult> {
    const body: Record<string, unknown> = { skill, target }
    if (notes.trim()) body.notes = notes.trim()
    return apiMutate<DraftResult>('/api/v1/output_skills/draft', {
      method: 'POST',
      body,
      failureLabel: 'Failed to draft',
    })
  }

  const isEmpty = computed(() => loaded.value && !needsAuth.value && skills.value.length === 0)

  return { skills, loaded, loading, loadError, needsAuth, isEmpty, fetchSkills, draftOutput }
})
